import React from 'react'
import { Image, ImageStyle, ScrollView, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useSelector } from 'react-redux'
import { useNavigation } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Button, Text } from '@components/base'
import { uxSensorModeKindSelector } from '@src/selectors/app'
import { UserExperienceSensorModeKind } from '@src/types'

import {
  libre1SensorBoxImage,
  libre2SensorBoxImage,
  libre3SensorBoxImage,
  dexcomG6BoxImage,
  dexcomG7BoxImage,
  dexcomSteloBoxImage,
  lingoBoxImage,
} from '@src/assets/images'
import { useTutorialNavigation } from '@src/screens/Tutorials/hooks'
import { useIsHealthPermissionRequired } from '@src/screens/OwnSensorOnboarding/utils'
import { AppStackParamList, CommonAuthorizedStackParamList } from '@src/navigation/types'
import { useLazyScreenPreload } from '@src/navigation/utils'
import { getHealthPermissionsModalStateInteractions } from '../Home/utils'
import { NavigationContainer } from '../Common/containers'
import {
  useByosOnboardingTutorial,
  useExitByosOnboarding,
  useShouldShowByosOnboarding,
} from './hooks'
import { OwnSensorOption } from './constants'

const SENSOR_BOX_IMAGES: Partial<
  {
    [key in UserExperienceSensorModeKind]: {
      imageSource: any
    }
  }
> = {
  [UserExperienceSensorModeKind.Default]: libre1SensorBoxImage,
  [UserExperienceSensorModeKind.Lingo]: lingoBoxImage,
  [UserExperienceSensorModeKind.OwnSensorLibre1]: libre1SensorBoxImage,
  [UserExperienceSensorModeKind.OwnSensorLibre2]: libre2SensorBoxImage,
  [UserExperienceSensorModeKind.OwnSensorLibre3]: libre3SensorBoxImage,
  [UserExperienceSensorModeKind.OwnSensorDexcomG6]: dexcomG6BoxImage,
  [UserExperienceSensorModeKind.OwnSensorDexcomG7]: dexcomG7BoxImage,
  [UserExperienceSensorModeKind.OwnSensorDexcomStelo]: dexcomSteloBoxImage,
  [UserExperienceSensorModeKind.DexcomStelo]: dexcomSteloBoxImage,
}

const SENSOR_NAMES: Partial<Record<UserExperienceSensorModeKind, OwnSensorOption>> = {
  [UserExperienceSensorModeKind.Default]: OwnSensorOption.FreeStyleLibre14Day,
  [UserExperienceSensorModeKind.Lingo]: OwnSensorOption.Lingo,
  [UserExperienceSensorModeKind.OwnSensorLibre1]: OwnSensorOption.FreeStyleLibre14Day,
  [UserExperienceSensorModeKind.OwnSensorLibre2]: OwnSensorOption.FreeStyleLibre2,
  [UserExperienceSensorModeKind.OwnSensorLibre3]: OwnSensorOption.FreeStyleLibre3,
  [UserExperienceSensorModeKind.OwnSensorDexcomG6]: OwnSensorOption.DexcomG6,
  [UserExperienceSensorModeKind.OwnSensorDexcomG7]: OwnSensorOption.DexcomG7,
  [UserExperienceSensorModeKind.OwnSensorDexcomStelo]: OwnSensorOption.DexcomStelo,
  [UserExperienceSensorModeKind.DexcomStelo]: OwnSensorOption.DexcomStelo,
}

export const ConfirmSensorSelectionScreen = () => {
  const styles = useStyleSheet(themedStyle)
  const insets = useSafeAreaInsets()
  const { shouldShow: shouldShowHealthPermissions } = getHealthPermissionsModalStateInteractions()

  const uxSensorModeKind = useSelector(uxSensorModeKindSelector)

  const isOnboardingInProgress = useShouldShowByosOnboarding()
  const tutorialSensorKind = useByosOnboardingTutorial()
  const navigateToTutorial = useTutorialNavigation(tutorialSensorKind)
  const exitByosOnboarding = useExitByosOnboarding()
  const appNavigation = useNavigation<StackNavigationProp<AppStackParamList>>()
  const authorizedNavigation = useNavigation<StackNavigationProp<CommonAuthorizedStackParamList>>()
  const isHealthPermissionRequired = useIsHealthPermissionRequired()

  const onContinuePress = () => {
    if (shouldShowHealthPermissions() && isHealthPermissionRequired) {
      appNavigation.navigate('HealthPermission')
      return
    }
    if (!tutorialSensorKind) {
      exitByosOnboarding()
      return
    }

    navigateToTutorial()
  }

  const changeSensorKind = () => {
    authorizedNavigation.navigate('OwnSensorSelection', { nextScreen: 'ConfirmSensorSelection' })
  }

  useLazyScreenPreload('AppExplorationPrompt', { shouldPreload: isOnboardingInProgress })

  const image = uxSensorModeKind && SENSOR_BOX_IMAGES[uxSensorModeKind]
  const sensorName = (uxSensorModeKind && SENSOR_NAMES[uxSensorModeKind]) || 'Unknown Sensor'
  const titleText = isOnboardingInProgress ? 'Welcome to Nutrisense!' : 'Choosing Your Sensor'
  const title = <Text type="title-3">{titleText}</Text>
  const onExitOnboardingPress = exitByosOnboarding

  return (
    <NavigationContainer
      title={title}
      leftIcon={{ pack: 'eva', name: 'close' }}
      goBack={isOnboardingInProgress ? onExitOnboardingPress : undefined}
    >
      <ScrollView
        contentContainerStyle={{
          flexGrow: 1,
          paddingTop: insets.top + 16,
          paddingBottom: insets.bottom + 16,
          justifyContent: 'space-between',
        }}
        bounces={false}
      >
        <View style={styles.contentContainer}>
          <Text type="title-3">Let's get you started</Text>
          <Text type="large" style={styles.text}>
            You mentioned that you have a
          </Text>
          {image && (
            <Image
              source={image.imageSource}
              style={styles.sensorBoxImage as ImageStyle}
              resizeMode="contain"
            />
          )}
          <Text type="regular" style={styles.text}>
            {sensorName}
          </Text>
        </View>

        <View style={styles.buttons}>
          <Button
            type="primary"
            size="block"
            accessibilityLabel="Continue"
            style={styles.button}
            onPress={onContinuePress}
          >
            Continue
          </Button>

          <Button
            type="outline"
            size="block"
            accessibilityLabel="Continue"
            style={styles.button}
            onPress={changeSensorKind}
          >
            Actually, I have a different sensor
          </Button>
        </View>
      </ScrollView>
    </NavigationContainer>
  )
}

const themedStyle = StyleService.create({
  contentContainer: {
    flex: 1,
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingBottom: 16,
    backgroundColor: 'theme.background',
  },
  buttons: {
    justifyContent: 'flex-end',
    flexDirection: 'column',
    alignItems: 'stretch',
    flex: 1,
    marginHorizontal: 24,
  },
  button: {
    marginTop: 16,
  },
  sensorBoxImage: {
    marginTop: 88,
    minWidth: 180,
    height: 180,
  },
  welcomeText: {
    marginBottom: 40,
  },
  text: {
    marginTop: 24,
  },
})
